import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../features/auth/authSlice";
import axios from "axios";
import { toast } from "react-toastify";
import { API_URL, SOCKET_URL } from "../config/apiConfig";
import socketService from "../utils/socketService";
import {
  initAdminNotifications,
  showBookingNotification,
} from "../utils/adminNotifications";
import {
  FaHome,
  FaTachometerAlt,
  FaUsers,
  FaCalendarAlt,
  FaTaxi,
  FaMapMarkerAlt,
  FaBuilding,
  FaBars,
  FaTimes,
  FaSignOutAlt,
  FaFileAlt,
  FaListAlt,
  FaGlobe,
  FaRoute,
  FaWallet,
  FaPlane,
  FaClock,
  FaChartBar,
  FaUserCheck,
  FaUserPlus,
  FaTicketAlt,
  FaStore,
  FaPercentage,
  FaCog,
  FaComments,
  FaNewspaper,
  FaMobileAlt,
  FaImage,
  FaWrench,
  FaExclamationTriangle,
  FaBell,
  FaUserShield,
  FaSyncAlt,
} from "react-icons/fa";

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [allowedSidebars, setAllowedSidebars] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const [currentTime, setCurrentTime] = useState("");
  const [currentDate, setCurrentDate] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [newBooking, setNewBooking] = useState(null);
  const [newLead, setNewLead] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [bookingSettings, setBookingSettings] = useState(null);

  // Automatically close sidebar on mobile when navigating to a new page
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Fetch public settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/booking/settings/public`);
        if (res.data && res.data.success) {
          setBookingSettings(res.data.data);
        }
      } catch (err) {
        console.error("Error fetching public settings in AdminLayout:", err);
      }
    };
    fetchSettings();
  }, []);

  // Load initial notifications on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem("admin_notifications");
      if (stored) {
        setNotifications(JSON.parse(stored));
      }
    } catch (e) {
      console.error("Failed to load notifications from localStorage:", e);
    }
  }, []);

  // Click outside listener for notification dropdown
  useEffect(() => {
    const handleOutsideClick = (e) => {
      const dropdown = document.getElementById("notification-dropdown-panel");
      const bellBtn = document.getElementById("notification-bell-btn");
      if (
        dropdown && 
        !dropdown.contains(e.target) && 
        bellBtn && 
        !bellBtn.contains(e.target)
      ) {
        setShowNotifications(false);
      }
    };
    if (showNotifications) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showNotifications]);

  const playNotificationSound = () => {
    try {
      const audio = new Audio('/notification.mp3');
      audio.play().catch(() => {});
    } catch (e) {}

    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = "sine";
      osc.frequency.setValueAtTime(587.33, ctx.currentTime); // D5
      osc.frequency.setValueAtTime(880.00, ctx.currentTime + 0.15); // A5
      
      gain.gain.setValueAtTime(0.5, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.start();
      osc.stop(ctx.currentTime + 0.4);
    } catch (err) {
      console.warn("Web Audio chime failed to play:", err);
    }
  };

  const userEmail = user?.data?.email || user?.user?.email || user?.email;
  const isMainAdmin = userEmail === "adarshkumar200201@gmail.com" || 
                      user?.isMainAdmin || 
                      user?.data?.isMainAdmin || 
                      user?.user?.isMainAdmin;

  const userRole = user?.data?.role || user?.user?.role || user?.role || "user";

  // Dynamic real-time system clock & date
  useEffect(() => {
    const updateTime = () => {
      const date = new Date();
      setCurrentTime(
        date.toLocaleTimeString(undefined, {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })
      );
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      setCurrentDate(`${day}/${month}/${year}`);
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleHeaderRefresh = () => {
    setIsRefreshing(true);
    window.dispatchEvent(new CustomEvent("admin:refresh"));
    window.dispatchEvent(new CustomEvent("adminRefreshBookings"));
    setTimeout(() => {
      setIsRefreshing(false);
    }, 800);
  };

  useEffect(() => {
    // Connect via centralized socket service
    const socket = socketService.connect();
    
    if (socket) {
      // Sync initial state and keep it verified
      const checkConnection = () => {
        const isCurrentlyConnected = socket.connected || socketService.isConnected;
        setSocketConnected(isCurrentlyConnected);
      };

      checkConnection();
      
      // Periodically check socket connection status (every 2 seconds) to keep the UI perfectly in sync
      const statusInterval = setInterval(checkConnection, 2000);
      
      // Emit join room (will auto-rejoin if it connects later or reconnects)
      socketService.joinAdminRoom();

      // ─── bookingCreated handler ────────────────────────────────────────────
      // IMPORTANT: Attach DIRECTLY to the raw socket (not through listenForEvent)
      // so it is never blocked by the shared retry-count limit. This guarantees
      // admin popup + sound fires for ALL booking sources (website AND mobile app).
      const handleNewBooking = (data) => {
        console.log("[Socket] 🔔 bookingCreated received:", data);
        setNewBooking(data);
        playNotificationSound();
        // Show OS-level browser notification (like WhatsApp) — works even when tab is minimized
        showBookingNotification(data);
        
        setNotifications((prev) => {
          // Prevent duplicate entries based on bookingId/bookingNumber
          const id = data.bookingId
            ? data.bookingId.toString()
            : data._id
            ? data._id.toString()
            : null;
          const exists = prev.some(
            (n) =>
              (id && n.id === id) ||
              (data.bookingNumber && n.bookingNumber === data.bookingNumber)
          );
          
          let updated;
          if (exists) {
            // Find existing notification and merge new info, marking it as unread (read: false)
            // and placing it at the top of the list!
            const existingNotif = prev.find(
              (n) =>
                (id && n.id === id) ||
                (data.bookingNumber && n.bookingNumber === data.bookingNumber)
            );
            const mergedNotif = {
              ...existingNotif,
              from: data.from || existingNotif.from || "—",
              to: data.to || existingNotif.to || "—",
              totalAmount: data.totalAmount || existingNotif.totalAmount,
              timestamp: data.timestamp || new Date().toISOString(),
              read: false, // Mark unread so notification badge increments and sounds
            };
            const filtered = prev.filter(
              (n) =>
                !((id && n.id === id) ||
                  (data.bookingNumber && n.bookingNumber === data.bookingNumber))
            );
            updated = [mergedNotif, ...filtered].slice(0, 20);
          } else {
            const newNotif = {
              id: id || Date.now().toString(),
              bookingNumber: data.bookingNumber,
              from: data.from || "—",
              to: data.to || "—",
              totalAmount: data.totalAmount,
              timestamp: data.timestamp || new Date().toISOString(),
              read: false,
            };
            updated = [newNotif, ...prev].slice(0, 20);
          }
          localStorage.setItem("admin_notifications", JSON.stringify(updated));
          return updated;
        });
      };

      // Attach bookingCreated listener
      socket.on("bookingCreated", handleNewBooking);

      // Attach abandonedBookingCreated listener for live dropped-off leads
      const handleAbandonedBooking = (data) => {
        console.log("[Socket] ⚡ abandonedBookingCreated received:", data);
        playNotificationSound();
        setNewLead(data);
        // Prevent duplicate toasts using unique toastId
        const toastId = `inquiry_${data.id || data.phone}`;
        toast.info(
          `⚡ New Booking Inquiry: ${data.name || "Customer"} (${data.phone}) on ${data.exitPage || "booking"}`,
          { autoClose: 7000, toastId }
        );

        setNotifications((prev) => {
          const id = data.id ? data.id.toString() : Date.now().toString();
          // Avoid duplicate notification entries
          if (prev.some((n) => n.id === id || (data.phone && n.bookingNumber.includes(data.phone)))) {
            return prev;
          }
          const newNotif = {
            id,
            isLead: true,
            bookingNumber: `Inquiry (${data.phone || "Guest"})`,
            from: data.pickup || "—",
            to: data.drop || "—",
            totalAmount: data.estimatedPrice,
            timestamp: data.timestamp || new Date().toISOString(),
            read: false,
            link: "/admin/abandoned-bookings",
          };
          const updated = [newNotif, ...prev].slice(0, 20);
          localStorage.setItem("admin_notifications", JSON.stringify(updated));
          return updated;
        });
      };

      socket.on("abandonedBookingCreated", handleAbandonedBooking);

      const handleConnect = () => {
        console.log("[Socket] ✅ Admin socket connected");
        setSocketConnected(true);
        // Rejoin admin room on every (re)connect
        socketService.joinAdminRoom();
      };
      
      const handleDisconnect = (reason) => {
        console.warn("[Socket] ❌ Admin socket disconnected:", reason);
        setSocketConnected(false);
      };
      
      socket.on("connect", handleConnect);
      socket.on("disconnect", handleDisconnect);
      
      return () => {
        clearInterval(statusInterval);
        socket.off("bookingCreated", handleNewBooking);
        socket.off("abandonedBookingCreated", handleAbandonedBooking);
        socket.off("connect", handleConnect);
        socket.off("disconnect", handleDisconnect);
      };
    }
  }, [userRole, isMainAdmin]);

  // ─── Browser Push Notifications (like WhatsApp) ───────────────────────────
  // Request permission & register service worker once admin is logged in.
  // This enables OS-level notifications that appear even when tab is not active.
  useEffect(() => {
    if (!userRole) return; // Only for logged-in admins
    initAdminNotifications().then((granted) => {
      if (granted) {
        console.log("[Notify] ✅ Browser notifications enabled for admin");
      }
    });
  }, [userRole]);


  useEffect(() => {
    const fetchPermissions = async () => {
      if (isMainAdmin) {
        setAllowedSidebars([]);
        setLoadingPermissions(false);
        return;
      }

      try {
        const token = localStorage.getItem("token") || (user && user.token);
        if (!token) return;
        const res = await axios.get(
          `${API_URL}/admin/role-permissions/${userRole}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (res.data.success && res.data.data) {
          setAllowedSidebars(res.data.data.allowedSidebars || []);
        }
      } catch (err) {
        console.error("Error fetching permissions:", err);
      } finally {
        setLoadingPermissions(false);
      }
    };

    fetchPermissions();
  }, [userRole, isMainAdmin, user]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/admin/login");
  };

  const menuItems = [
    { path: "/", name: "Go to Website", icon: <FaGlobe /> },
    { path: "/admin/home", name: "Home Directory", icon: <FaHome /> },
    { path: "/admin/dashboard", name: "Dashboard", icon: <FaTachometerAlt /> },
    { path: "/admin/roles", name: "Role Management", icon: <FaUsers /> },
    { path: "/admin/role-permissions", name: "Role Permissions", icon: <FaUserCheck /> },
    { path: "/admin/create-staff", name: "Create Staff", icon: <FaUserPlus /> },
    { path: "/admin/users", name: "Users", icon: <FaUsers /> },
    { path: "/admin/bookings", name: "Bookings", icon: <FaCalendarAlt /> },
    {
      path: "/admin/abandoned-bookings",
      name: "Booking Inquiries",
      icon: <FaClock className="text-amber-400" />,
    },
    { path: "/admin/live-tracking", name: "Live Tracking", icon: <FaMapMarkerAlt /> },
    {
      path: "/admin/ride-issues",
      name: "Ride Issues",
      icon: <FaExclamationTriangle className="text-red-400" />,
    },
    { path: "/admin/corporates", name: "Corporate", icon: <FaBuilding /> },
    { path: "/admin/corporate-wallet", name: "Corporate Wallet", icon: <FaWallet /> },
    { path: "/admin/cabs", name: "Cab Management", icon: <FaTaxi /> },
    { path: "/admin/drivers", name: "Driver Management", icon: <FaUsers /> },
    { path: "/admin/vendors", name: "Vendor Management", icon: <FaStore /> },
    { path: "/admin/commission", name: "Commission Management", icon: <FaPercentage /> },
    { path: "/admin/airports", name: "Airport Management", icon: <FaPlane /> },
    {
      path: "/admin/rental-packages",
      name: "Rental Packages",
      icon: <FaClock />,
    },
    {
      path: "/admin/coupons",
      name: "Coupon Management",
      icon: <FaTicketAlt />,
    },
    {
      path: "/admin/wallet/summary",
      name: "Wallet Summary",
      icon: <FaChartBar />,
    },
    {
      path: "/admin/wallet/drivers",
      name: "Driver Wallets",
      icon: <FaUserCheck />,
    },
    {
      path: "/admin/wallet/withdrawals",
      name: "Pending Withdrawals",
      icon: <FaWallet />,
    },
    {
      path: "/admin/wallet/user-withdrawals",
      name: "User Withdrawals",
      icon: <FaWallet />,
    },
    {
      path: "/admin/popular-routes",
      name: "Fixed Routes",
      icon: <FaListAlt />,
    },
    {
      path: "/admin/popular-cities",
      name: "Popular Cities",
      icon: <FaGlobe />,
    },
    { path: "/admin/footer-routes", name: "Premium Routes", icon: <FaRoute /> },
    {
      path: "/admin/document-settings",
      name: "Document Requirements",
      icon: <FaFileAlt />,
    },
    {
      path: "/admin/booking-settings",
      name: "Booking Settings",
      icon: <FaCog />,
    },
    {
      path: "/admin/app-version",
      name: "App Version",
      icon: <FaMobileAlt />,
    },
    {
      path: "/admin/testimonials",
      name: "Testimonials",
      icon: <FaComments />,
    },
    {
      path: "/admin/media",
      name: "Media Management",
      icon: <FaFileAlt />,
    },
    {
      path: "/admin/ads",
      name: "Ads Management",
      icon: <FaImage />,
    },
    {
      path: "/admin/blogs",
      name: "Blog Management",
      icon: <FaNewspaper />,
    },
    {
      path: "/admin/support-details",
      name: "Support Details",
      icon: <FaComments />,
    },
    {
      path: "/admin/support-messages",
      name: "Support Messages",
      icon: <FaComments />,
    },
    {
      path: "/admin/maintenance",
      name: "Maintenance Mode",
      icon: <FaWrench />,
    },
    {
      path: "/admin/auth-images",
      name: "Auth Page Images",
      icon: <FaImage />,
    },
  ];

  const filteredMenuItems = menuItems.filter((item) => {
    // Check global module settings first
    if (bookingSettings) {
      if (bookingSettings.enableVendorModule === false) {
        if (
          item.path === "/admin/vendors" || 
          item.path === "/admin/commission" ||
          item.path === "/admin/wallet/withdrawals"
        ) {
          return false;
        }
      }
      if (bookingSettings.enableAdminDriverModule === false) {
        if (
          item.path === "/admin/drivers" || 
          item.path === "/admin/wallet/drivers"
        ) {
          return false;
        }
      }
    }

    if (item.path === "/" || item.path === "/admin/home") return true;
    
    // Main admin sees everything
    if (isMainAdmin) {
      return true;
    }

    // Non-main admin (other than standard 'admin' role) can never see roles & permissions management
    if (
      (item.path === "/admin/roles" ||
      item.path === "/admin/role-permissions" ||
      item.path === "/admin/create-staff") &&
      userRole !== "admin"
    ) {
      return false;
    }

    // Default admin role fallback (if no permissions configured, allow access to all)
    if (userRole === "admin" && allowedSidebars.length === 0) {
      return true;
    }

    // Check if the path is allowed
    return allowedSidebars.includes(item.path);
  });

  const sections = [
    {
      title: "General",
      items: ["Go to Website", "Home Directory", "Dashboard"]
    },
    {
      title: "Operations & Bookings",
      items: ["Bookings", "Booking Inquiries", "Live Tracking", "Ride Issues", "Coupon Management"]
    },
    {
      title: "Partner & Fleet",
      items: ["Cab Management", "Driver Management", "Vendor Management"]
    },
    {
      title: "Finance & Commission",
      items: [
        "Corporate",
        "Corporate Wallet",
        "Commission Management",
        "Wallet Summary",
        "Driver Wallets",
        "Pending Withdrawals",
        "User Withdrawals"
      ]
    },
    {
      title: "Travel Settings",
      items: ["Airport Management", "Rental Packages", "Fixed Routes", "Premium Routes", "Popular Cities"]
    },
    {
      title: "System Config & Content",
      items: [
        "Role Management",
        "Role Permissions",
        "Create Staff",
        "Users",
        "Document Requirements",
        "Booking Settings",
        "App Version",
        "Testimonials",
        "Media Management",
        "Ads Management",
        "Blog Management",
        "Support Details",
        "Support Messages",
        "Maintenance Mode",
        "Auth Page Images"
      ]
    }
  ];

  const getPageTitle = (path) => {
    const item = menuItems.find(
      (m) =>
        m.path === path ||
        (m.path !== "/" && m.path !== "/admin/dashboard" && path.startsWith(m.path))
    );
    return item ? item.name : "Admin Panel";
  };

  const isPathAllowed = (path) => {
    if (path === "/" || path === "/admin/home") return true;
    if (isMainAdmin) {
      return true;
    }

    const normalizedPath = path.toLowerCase().replace(/\/$/, "");

    // Block non-main admin (other than standard 'admin' role) from roles & permissions management and create-staff entirely
    const systemBlockedPaths = [
      "/admin/roles",
      "/admin/role-permissions",
      "/admin/create-staff"
    ];

    if (userRole !== "admin" && systemBlockedPaths.some(p => normalizedPath === p || normalizedPath.startsWith(p + "/"))) {
      return false;
    }

    // Default admin role fallback (if no permissions configured, allow access to all)
    if (userRole === "admin" && allowedSidebars.length === 0) {
      return true;
    }

    // If pathname is exactly "/admin", check if either "/admin" or "/admin/dashboard" is allowed
    if (normalizedPath === "/admin" || normalizedPath === "/admin/dashboard") {
      return allowedSidebars.includes("/admin/dashboard") || allowedSidebars.includes("/admin");
    }

    // Check if the current path matches any of the allowed sidebars (including subpaths)
    return allowedSidebars.some((allowedPath) => {
      const normalizedAllowed = allowedPath.toLowerCase().replace(/\/$/, "");
      return normalizedPath === normalizedAllowed || normalizedPath.startsWith(normalizedAllowed + "/");
    });
  };

  return (
    <div className="flex h-screen overflow-hidden font-sans">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-20 transition-opacity duration-300"
          onClick={() => {
            console.log("[Sidebar] Backdrop clicked! Closing...");
            setSidebarOpen(false);
          }}
        ></div>
      )}

      {/* Sidebar - Dark Glassmorphism aesthetic */}
      <aside
        className={`admin-sidebar-responsive fixed top-0 z-30 w-64 h-screen transition-all duration-300 ease-in-out bg-gradient-to-b from-[#0F172A] via-[#1E293B] to-[#0F172A] text-slate-300 ${
          sidebarOpen ? "left-0" : "-left-64"
        } md:left-0 md:relative shadow-2xl flex flex-col`}
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-800/80 bg-slate-950/20">
          <Link to="/admin" onClick={() => setSidebarOpen(false)} className="flex items-center gap-2.5 group">
            <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-md shadow-blue-500/20 group-hover:scale-105 transition-transform duration-200">
              <FaUserShield className="text-sm" />
            </div>
            <span className="text-sm font-black tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-white via-slate-100 to-slate-300">
              CABZIGO ADMIN
            </span>
          </Link>
          <button
            onClick={() => {
              console.log("[Sidebar] X button clicked! Closing...");
              setSidebarOpen(false);
            }}
            className="md:hidden text-slate-400 hover:text-white transition-colors duration-150 focus:outline-none p-2 hover:bg-slate-800/60 rounded-xl flex items-center justify-center"
            aria-label="Close sidebar"
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* Sidebar Menu - Scrollable Area */}
        <div className="flex-1 overflow-y-auto py-2 px-3 custom-scrollbar">
          <nav className="space-y-2">
            {sections.map((section) => {
              const sectionItems = filteredMenuItems.filter((item) =>
                section.items.includes(item.name)
              );

              if (sectionItems.length === 0) return null;

              return (
                <div key={section.title} className="mb-4">
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest px-3 py-1.5 mt-2">
                    {section.title}
                  </div>
                  <div className="space-y-1">
                    {sectionItems.map((item, index) => {
                      const isActive =
                        location.pathname === item.path ||
                        (item.path !== "/admin/dashboard" &&
                          item.path !== "/" &&
                          location.pathname.startsWith(item.path));

                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setSidebarOpen(false)}
                          className={`relative flex items-center px-4 py-2.5 text-[12px] font-semibold rounded-xl transition-all duration-150 gap-3 group ${
                            isActive
                              ? "bg-gradient-to-r from-blue-600/90 to-indigo-600/90 text-white shadow-lg shadow-blue-500/10"
                              : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/30"
                          }`}
                        >
                          {isActive && (
                            <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-gradient-to-b from-blue-400 to-indigo-400 rounded-r-full"></span>
                          )}
                          <span
                            className={`text-sm transition-transform duration-150 group-hover:scale-110 ${
                              isActive ? "text-white" : "text-slate-400 group-hover:text-slate-200"
                            }`}
                          >
                            {item.icon}
                          </span>
                          <span>{item.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </nav>
        </div>

        {/* User Profile & Logout - Sticky Footer */}
        <div className="border-t border-slate-800/80 bg-slate-950/30 p-4 mt-auto">
          <div className="flex items-center mb-4">
            <div className="mr-3 h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-indigo-600/20 border border-blue-500/30 flex items-center justify-center text-blue-400 font-black ring-2 ring-blue-500/5">
              {(user?.data?.name || user?.user?.name || user?.name || "Admin User")
                .charAt(0)
                .toUpperCase()}
            </div>
            <div className="overflow-hidden">
              <p className="text-xs font-bold text-white truncate">
                {user?.data?.name || user?.user?.name || user?.name || "Admin User"}
              </p>
              <p className="text-[10px] text-slate-500 truncate mt-0.5">
                {user?.data?.email || user?.user?.email || user?.email || "admin@example.com"}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center justify-center w-full px-4 py-2.5 text-xs font-bold text-white bg-slate-800/80 border border-slate-700/50 rounded-xl hover:bg-red-600/90 hover:border-red-500/40 transition-all duration-150 active:scale-[0.98]"
          >
            <FaSignOutAlt className="mr-2 text-sm" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex flex-col flex-1 transition-all duration-300 bg-slate-50 overflow-hidden">
        {/* Universal Top Header */}
        <header className="admin-header-bar relative z-10 bg-white border-b border-slate-100 shadow-sm px-4 md:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-slate-500 hover:text-slate-700 focus:outline-none md:hidden p-2 hover:bg-slate-100 rounded-xl transition-colors flex items-center justify-center"
              aria-label="Open sidebar"
            >
              <FaBars size={20} />
            </button>
            <div className="hidden md:flex flex-col">
              <span className="text-[10px] font-black text-blue-600 tracking-widest uppercase">
                Administrative Portal
              </span>
              <h2 className="text-md font-black text-slate-800 tracking-tight mt-0.5">
                {getPageTitle(location.pathname)}
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* Socket Connection Status Widget */}
            <div className={`hidden xl:flex items-center gap-2 text-[11px] font-bold border rounded-full px-3 py-1.5 shadow-sm transition-all duration-300 ${
              socketConnected 
                ? "text-emerald-600 bg-emerald-50 border-emerald-100" 
                : "text-amber-600 bg-amber-50 border-amber-100 animate-pulse"
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${
                socketConnected ? "bg-emerald-500 animate-pulse" : "bg-amber-500"
              }`}></span>
              <span>{socketConnected ? "Live Updates: Active" : "Live Updates: Offline"}</span>
            </div>

            {/* Realtime Time & Date Widget */}
            {currentTime && (
              <div className="flex items-center gap-2 text-[11px] font-bold text-slate-600 bg-slate-50 border border-slate-200/80 rounded-full px-3 py-1.5 shadow-2xs">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                <span className="font-mono text-slate-800 font-semibold">{currentTime}</span>
                {currentDate && (
                  <>
                    <span className="text-slate-300">|</span>
                    <span className="text-slate-600 hidden sm:inline">{currentDate}</span>
                  </>
                )}
              </div>
            )}

            {/* Global Admin Refresh Button */}
            <button
              onClick={handleHeaderRefresh}
              disabled={isRefreshing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 active:scale-95 transition-all duration-150 rounded-xl shadow-sm hover:shadow cursor-pointer disabled:opacity-70"
              title="Refresh page data"
              aria-label="Refresh data"
            >
              <FaSyncAlt className={`text-xs ${isRefreshing ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>

            {/* Notification Bell & Dropdown */}
            <div className="relative">
              <button
                id="notification-bell-btn"
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative cursor-pointer hover:bg-slate-50 p-2 rounded-xl border border-transparent hover:border-slate-100 transition-all duration-150 flex items-center justify-center focus:outline-none"
                title="Notifications"
              >
                <FaBell className="text-slate-500 text-base" />
                {notifications.filter((n) => !n.read).length > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-red-500 text-white text-[9px] font-black rounded-full ring-2 ring-white flex items-center justify-center animate-pulse">
                    {notifications.filter((n) => !n.read).length}
                  </span>
                )}
              </button>

              {showNotifications && (
                <div
                  id="notification-dropdown-panel"
                  className="absolute right-0 mt-2 w-80 bg-white/95 backdrop-blur-md rounded-2xl border border-slate-100 shadow-2xl z-50 overflow-hidden animate-fade-in"
                >
                  <div className="flex items-center justify-between px-4 py-3 bg-slate-50/80 border-b border-slate-100">
                    <span className="text-xs font-black text-slate-800">
                      Notifications ({notifications.length})
                    </span>
                    <div className="flex gap-2">
                      {notifications.length > 0 && (
                        <>
                          <button
                            onClick={() => {
                              const updated = notifications.map((n) => ({ ...n, read: true }));
                              setNotifications(updated);
                              localStorage.setItem("admin_notifications", JSON.stringify(updated));
                            }}
                            className="text-[10px] font-bold text-blue-600 hover:text-blue-800 transition cursor-pointer"
                          >
                            Mark all as read
                          </button>
                          <span className="text-slate-300 text-[10px]">|</span>
                          <button
                            onClick={() => {
                              setNotifications([]);
                              localStorage.removeItem("admin_notifications");
                            }}
                            className="text-[10px] font-bold text-red-500 hover:text-red-700 transition cursor-pointer"
                          >
                            Clear all
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="max-h-64 overflow-y-auto custom-scrollbar">
                    {notifications.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                        <FaBell className="text-2xl mb-2 opacity-55" />
                        <span className="text-xs font-bold">No new notifications</span>
                      </div>
                    ) : (
                      notifications.map((n) => {
                        const timeString = new Date(n.timestamp).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        });
                        return (
                          <div
                            key={n.id}
                            onClick={() => {
                              // Mark as read
                              const updated = notifications.map((item) =>
                                item.id === n.id ? { ...item, read: true } : item
                              );
                              setNotifications(updated);
                              localStorage.setItem("admin_notifications", JSON.stringify(updated));
                              setShowNotifications(false);
                              if (n.link) {
                                navigate(n.link);
                              } else {
                                navigate(`/admin/bookings?search=${n.bookingNumber || n.id}`);
                              }
                            }}
                            className={`flex gap-3 px-4 py-3 border-b border-slate-50 cursor-pointer hover:bg-slate-50/80 transition-colors duration-150 relative ${
                              !n.read ? "bg-blue-50/30" : ""
                            }`}
                          >
                            {!n.read && (
                              <span className="absolute left-1.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-blue-600 rounded-full"></span>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-slate-500 font-mono">
                                  #{n.bookingNumber}
                                </span>
                                <span className="text-[9px] font-bold text-slate-400">
                                  {timeString}
                                </span>
                              </div>
                              <p className="text-[11px] font-bold text-slate-700 truncate mt-0.5">
                                {n.from} → {n.to}
                              </p>
                              <p className="text-[10px] font-semibold text-emerald-600 mt-0.5">
                                Amount: ₹{n.totalAmount}
                              </p>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* User Dropdown Widget */}
            <div className="flex items-center gap-2.5 border-l border-slate-100 pl-4">
              <div className="h-8.5 w-8.5 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-black shadow-md shadow-blue-500/10">
                {(user?.data?.name || user?.user?.name || user?.name || "Admin")
                  .charAt(0)
                  .toUpperCase()}
              </div>
              <div className="hidden sm:flex flex-col text-left">
                <span className="text-xs font-black text-slate-800">
                  {user?.data?.name || user?.user?.name || user?.name || "Admin User"}
                </span>
                <span className="text-[10px] text-slate-400 capitalize font-bold mt-0.5">
                  {userRole}
                </span>
              </div>
            </div>

            {/* Header Logout Button */}
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-red-600 hover:text-white bg-rose-50 hover:bg-red-600 border border-rose-100 hover:border-red-600 rounded-xl transition-all duration-150 active:scale-[0.98] cursor-pointer"
              title="Logout"
            >
              <FaSignOutAlt className="text-xs" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </header>

        {/* Main Content Body */}
        <main className={`admin-main flex-1 bg-slate-50 p-0 ${
          location.pathname === "/admin/live-tracking"
            ? "overflow-hidden flex flex-col"
            : "overflow-y-auto"
        }`}>
          <div key={location.pathname} className={`animate-fade-in-slide-up w-full ${
            location.pathname === "/admin/live-tracking"
              ? "h-full flex flex-col flex-1"
              : "min-h-full"
          }`}>
            {loadingPermissions ? (
              <div className="flex flex-col items-center justify-center min-h-[70vh] bg-slate-50/50">
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin"></div>
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-blue-600 font-bold text-xs">
                    Admin
                  </div>
                </div>
                <p className="mt-4 text-xs font-bold text-slate-500 tracking-wider animate-pulse">
                  VERIFYING SECURITY CREDENTIALS...
                </p>
              </div>
            ) : isPathAllowed(location.pathname) ? (
              children
            ) : (
              <div className="flex items-center justify-center min-h-[75vh] px-4 bg-slate-50">
                <div className="max-w-md w-full bg-white rounded-3xl border border-slate-100 shadow-xl p-8 text-center relative overflow-hidden group">
                  {/* Decorative background glows */}
                  <div className="absolute -top-24 -left-24 w-48 h-48 bg-rose-500/5 rounded-full blur-3xl group-hover:bg-rose-500/10 transition-colors duration-500"></div>
                  <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-indigo-500/5 rounded-full blur-3xl group-hover:bg-indigo-500/10 transition-colors duration-500"></div>

                  {/* Glowing Glass Icon Container */}
                  <div className="relative mx-auto w-24 h-24 bg-gradient-to-tr from-rose-50 to-orange-50 border border-rose-100/60 rounded-3xl flex items-center justify-center text-rose-500 shadow-lg shadow-rose-500/5 mb-6 group-hover:scale-105 transition-transform duration-300">
                    <div className="absolute inset-0 bg-rose-500/5 rounded-3xl animate-ping opacity-30"></div>
                    <FaUserShield className="text-4xl filter drop-shadow-sm" />
                  </div>

                  {/* Heading */}
                  <h1 className="text-3xl font-black text-slate-800 tracking-tight mb-2">
                    Access Restricted
                  </h1>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-rose-50 text-rose-600 text-[10px] font-black tracking-wider uppercase rounded-full mb-4">
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse"></span>
                    Error Code: 403 Forbidden
                  </div>

                  {/* Description */}
                  <p className="text-sm text-slate-500 leading-relaxed mb-8 max-w-sm mx-auto">
                    This page is not available or your administrative role (<span className="text-slate-700 font-bold">{userRole}</span>) does not have permission to access it. Please contact the main administrator to update your access control permissions.
                  </p>

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={() => navigate("/admin/dashboard")}
                      className="w-full py-3.5 px-6 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold text-xs rounded-2xl shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30 transition-all duration-200 active:scale-[0.98]"
                    >
                      Return to Dashboard
                    </button>
                    <button
                      onClick={() => navigate(-1)}
                      className="w-full py-3.5 px-6 bg-slate-50 hover:bg-slate-100 text-slate-600 border border-slate-200/60 font-bold text-xs rounded-2xl transition-all duration-150 active:scale-[0.98]"
                    >
                      Go Back
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* New Booking Socket Notification Toast Popup */}
      {newBooking && (
        <div className="fixed bottom-6 right-6 z-50 max-w-sm w-full bg-white/95 backdrop-blur-md rounded-2xl border border-rose-100 shadow-2xl p-5 animate-bounce-short transition-all duration-300">
          <div className="flex gap-3">
            <div className="h-10 w-10 rounded-full bg-rose-50 flex items-center justify-center text-red-600 flex-shrink-0 animate-pulse">
              <FaBell size={18} />
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <h4 className="text-sm font-black text-slate-800">
                  New Booking Received!
                </h4>
                <button 
                  onClick={() => setNewBooking(null)}
                  className="text-slate-400 hover:text-slate-600 transition"
                >
                  <FaTimes size={14} />
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1 font-semibold">
                Booking ID: <span className="font-mono text-blue-600 font-bold">{newBooking.bookingNumber}</span>
              </p>
              <p className="text-xs text-slate-600 mt-1.5 font-bold">
                Route: {newBooking.from} → {newBooking.to}
              </p>
              <p className="text-xs text-slate-600 mt-1 font-bold">
                Amount: ₹{newBooking.totalAmount}
              </p>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => {
                    setNewBooking(null);
                    navigate(`/admin/bookings?search=${newBooking.bookingNumber || newBooking.bookingId || newBooking._id}`);
                  }}
                  className="flex-1 text-center py-2 px-3 bg-[#7F1D1D] hover:bg-[#591414] text-white rounded-xl text-[11px] font-bold shadow-md shadow-red-700/20 transition-all duration-150 active:scale-[0.98]"
                >
                  Manage Booking
                </button>
                <button
                  onClick={() => setNewBooking(null)}
                  className="py-2 px-3 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-[11px] font-bold transition-all duration-150 active:scale-[0.98]"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Abandoned Booking Socket Notification Toast Popup */}
      {newLead && (
        <div className="fixed bottom-6 right-6 z-50 max-w-sm w-full bg-white/95 backdrop-blur-md rounded-2xl border border-amber-300 shadow-2xl p-5 animate-bounce-short transition-all duration-300">
          <div className="flex gap-3">
            <div className="h-10 w-10 rounded-full bg-amber-50 flex items-center justify-center text-amber-600 flex-shrink-0 animate-pulse">
              <FaClock size={18} />
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <h4 className="text-sm font-black text-slate-800 flex items-center gap-1.5">
                  <span className="text-amber-500">⚡</span> New Booking Inquiry!
                </h4>
                <button 
                  onClick={() => setNewLead(null)}
                  className="text-slate-400 hover:text-slate-600 transition"
                >
                  <FaTimes size={14} />
                </button>
              </div>
              <p className="text-xs text-slate-700 mt-1 font-bold">
                {newLead.name || "Customer"} • <span className="font-mono text-blue-600">{newLead.phone}</span>
              </p>
              <p className="text-xs text-slate-600 mt-1 truncate" title={`${newLead.pickup} → ${newLead.drop}`}>
                {newLead.pickup} → {newLead.drop}
              </p>
              <p className="text-xs text-emerald-700 mt-1 font-bold">
                Est. Fare: ₹{newLead.estimatedPrice ? Number(newLead.estimatedPrice).toLocaleString("en-IN") : "—"}
              </p>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => {
                    setNewLead(null);
                    navigate("/admin/abandoned-bookings");
                  }}
                  className="flex-1 text-center py-2 px-3 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-[11px] font-bold shadow-md shadow-amber-600/20 transition-all duration-150 active:scale-[0.98]"
                >
                  View Inquiry
                </button>
                <button
                  onClick={() => setNewLead(null)}
                  className="py-2 px-3 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-[11px] font-bold transition-all duration-150 active:scale-[0.98]"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLayout;
